import _ from "lodash";
import { useCallback } from "react";
import { PECScanData } from "../utils/models";
import { getWallThicknessValue } from "../utils/utilities";

interface ThicknessLocationData {
    srNo: any;
    axialLocation: string;
    minThicknessValue: number;
}

const usePecScanOverview = () => {
    const calculatePecScanSummary = useCallback((pecScan: PECScanData) => {
        const allReadingValues = pecScan.readings.data.map((item) => Object.values(_.omit(item, ['srNo'])).flat()).flat().filter((reading) => !isNaN(parseFloat(reading)));
        const parseReadingValues = allReadingValues.map((thickness) => parseFloat(thickness));
        const averageThickness = parseReadingValues.reduce((a, c) => a + c, 0) / parseReadingValues.length;
      
        const minimumThickness = Math.min(...parseReadingValues);
        const maximumThickness = Math.max(...parseReadingValues);
      
        const axialValues = pecScan.readings.data.map((item) => parseFloat(item.srNo));
      
        const getMinThicknessLocation = (item: any): ThicknessLocationData | null => {
          const itemWithoutAxial = _.omit(item, ['srNo']);
          const keyMatch = Object.keys(itemWithoutAxial).find((key) => +itemWithoutAxial[key]  === minimumThickness);
      
          if (keyMatch) {
              return {
                srNo: item.srNo,
                axialLocation: keyMatch,
                minThicknessValue: minimumThickness,
              }
          }
      
          return null;
        }
       
        const minThicknessLocations =  pecScan.readings.data.filter(getMinThicknessLocation).map(getMinThicknessLocation);
        if (minThicknessLocations.length > 0) {
            const summaryData = minThicknessLocations.map((location: ThicknessLocationData | null) => ({
                sectionName: pecScan.sectionName,
                axialLength: Math.max(...pecScan.readings.circValues) - Math.min(...pecScan.readings.circValues),
                circLength: Math.max(...axialValues) - Math.min(...axialValues),
                nominalThickness: pecScan.nominalThickness,
                corrosionAllowance: pecScan.corrosionAllowance,
                minimumThickness: location?.minThicknessValue?.toFixed(2),
                wallThickness: getWallThicknessValue(pecScan.nominalThickness, minimumThickness).toFixed(2),
                axialLocation: location?.axialLocation,
                circLocation: location?.srNo,
                maximumThickness: isNaN(maximumThickness) ? 'Nil' : maximumThickness.toFixed(2),
                averageThickness: averageThickness.toFixed(2),
                remarks: pecScan.remarks,
                result: pecScan.result
            }));
    
            return summaryData;
        }

        return [
            {
                sectionName: pecScan.sectionName,
                axialLength: 'Nil',
                circLength: 'Nil',
                nominalThickness: pecScan.nominalThickness,
                corrosionAllowance: pecScan.corrosionAllowance,
                minimumThickness: 'Nil',
                wallThickness: 'Nil',
                axialLocation: 'Nil',
                circLocation: 'Nil',
                maximumThickness: 'Nil',
                averageThickness: 'Nil',
                remarks: pecScan.remarks,
                result: pecScan.result
            }
        ]
    }, []);

    return calculatePecScanSummary;
}
 
export default usePecScanOverview;